import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import { 
  FaChartLine, 
  FaPiggyBank, 
  FaShieldAlt, 
  FaHandshake, 
  FaBalanceScale, 
  FaChartPie,
  FaUserTie,
  FaBriefcase,
  FaGraduationCap,
  FaCog,
  FaUsers,
  FaFileContract,
  FaArrowRight
} from 'react-icons/fa';

const ServicesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Services' }
  ];

  const coreServices = [
    {
      icon: <FaChartPie className="w-8 h-8 text-secondary" />,
      title: "Mutual Funds",
      description: "Diversified investment solutions with professional fund management across equity, fixed income, and alternative asset classes.",
      link: "/services/mutual-funds",
      features: ["Equity Funds", "Balanced Funds", "Fixed Income", "Sector Specific"]
    },
    {
      icon: <FaChartLine className="w-8 h-8 text-secondary" />,
      title: "Investment Management",
      description: "Comprehensive investment management services tailored to your financial goals with expert portfolio construction.",
      link: "/services/investment-management",
      features: ["Portfolio Construction", "Risk Management", "Performance Monitoring", "Strategic Allocation"]
    },
    {
      icon: <FaPiggyBank className="w-8 h-8 text-secondary" />,
      title: "Personal Retirement Account",
      description: "Tax-advantaged retirement planning with various IRA options to secure your financial future.",
      link: "/services/personal-retirement-account",
      features: ["Traditional IRA", "Roth IRA", "SEP-IRA", "Self-Directed IRA"]
    },
    {
      icon: <FaHandshake className="w-8 h-8 text-secondary" />,
      title: "Securities Trading & Brokerage",
      description: "Professional trading services with access to global markets and institutional-grade execution.",
      link: "/services/securities-trading-brokerage",
      features: ["Equity Trading", "Fixed Income", "Options & Derivatives", "FX Trading"]
    },
    {
      icon: <FaFileContract className="w-8 h-8 text-secondary" />,
      title: "Trustee & Registrar Services",
      description: "Professional fiduciary services with comprehensive trust administration and regulatory compliance.",
      link: "/services/trustee-registrar-services",
      features: ["Corporate Trustee", "Fund Administration", "Fiduciary Services", "Compliance Monitoring"]
    },
    {
      icon: <FaBalanceScale className="w-8 h-8 text-secondary" />,
      title: "Wealth Management",
      description: "Holistic wealth management solutions for high-net-worth individuals and institutional clients.",
      link: "/services/wealth-management",
      features: ["Portfolio Management", "Estate Planning", "Tax Optimization", "Family Office Services"]
    }
  ];

  const additionalServices = [
    {
      icon: <FaUserTie className="w-8 h-8 text-secondary" />,
      title: "Financial Planning",
      description: "Comprehensive financial planning to achieve your long-term financial objectives.",
      link: "/services/financial-planning"
    },
    {
      icon: <FaGraduationCap className="w-8 h-8 text-secondary" />,
      title: "Retirement Planning",
      description: "Strategic retirement planning to ensure financial security in your golden years.",
      link: "/services/retirement-planning"
    },
    {
      icon: <FaBriefcase className="w-8 h-8 text-secondary" />,
      title: "Managed Portfolios",
      description: "Professionally managed portfolios with active oversight and strategic rebalancing.",
      link: "/services/managed-portfolios"
    },
    {
      icon: <FaShieldAlt className="w-8 h-8 text-secondary" />,
      title: "Wealth & Estate Planning",
      description: "Comprehensive estate planning services to preserve and transfer your wealth.",
      link: "/services/wealth-estate-planning"
    },
    {
      icon: <FaCog className="w-8 h-8 text-secondary" />,
      title: "Financial Advisory",
      description: "Expert financial advisory services for complex financial planning needs.",
      link: "/services/financial-advising"
    },
    {
      icon: <FaUsers className="w-8 h-8 text-secondary" />,
      title: "Portfolio Management",
      description: "Active portfolio management with institutional-quality investment strategies.",
      link: "/services/portfolio-management"
    }
  ];

  return (
    <>
      <PageHeader 
        title="Our Services" 
        breadcrumbs={breadcrumbs}
        backgroundImage="/assets/business-colleagues-banner-concept-with-copy-space.jpg"
      />
      
      <section className="py-16 md:py-24 bg-background">
        <div className="container-custom">
          {/* Introduction */}
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-heading font-bold text-primary mb-6">
                Comprehensive Financial Solutions
              </h2>
              <p className="text-xl text-gray-700 max-w-4xl mx-auto">
                <span className="font-semibold">Segway Capital</span> offers a complete suite of financial services designed to meet 
                the diverse needs of individual investors, high-net-worth clients, and institutional investors. 
                Our expert team combines decades of experience with cutting-edge technology to deliver superior results.
              </p>
            </motion.div>
          </div>

          {/* Core Services */}
          <div className="mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h3 className="text-3xl font-heading font-bold text-primary mb-4">Core Services</h3>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Our flagship services that form the foundation of our comprehensive financial solutions.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {coreServices.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl p-8 shadow-custom hover:shadow-custom-lg transition-all duration-300 group"
                >
                  <div className="bg-secondary/10 p-4 rounded-full w-16 h-16 flex items-center justify-center mb-6 group-hover:bg-secondary/20 transition-colors duration-300">
                    {service.icon}
                  </div>
                  <h4 className="text-xl font-heading font-semibold mb-4 text-primary">{service.title}</h4>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  
                  {service.features && (
                    <div className="mb-6">
                      <h5 className="text-sm font-semibold text-primary mb-3">Key Features:</h5>
                      <div className="grid grid-cols-2 gap-2">
                        {service.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center text-sm text-gray-600">
                            <div className="w-1.5 h-1.5 bg-secondary rounded-full mr-2"></div>
                            {feature}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <Link 
                    to={service.link}
                    className="inline-flex items-center text-secondary font-semibold hover:text-primary transition-colors duration-300 group"
                  >
                    Learn More 
                    <FaArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Additional Services */}
          <div className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h3 className="text-3xl font-heading font-bold text-primary mb-4">Additional Services</h3>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Specialized services to complement your comprehensive financial strategy.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {additionalServices.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl p-6 shadow-custom hover:shadow-custom-lg transition-all duration-300 group"
                >
                  <div className="bg-secondary/10 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4 group-hover:bg-secondary/20 transition-colors duration-300">
                    {service.icon}
                  </div>
                  <h4 className="text-lg font-heading font-semibold mb-3 text-primary">{service.title}</h4>
                  <p className="text-gray-600 mb-4 text-sm">{service.description}</p>
                  <Link 
                    to={service.link}
                    className="inline-flex items-center text-secondary font-semibold hover:text-primary transition-colors duration-300 group text-sm"
                  >
                    Learn More 
                    <FaArrowRight className="ml-2 w-3 h-3 group-hover:translate-x-1 transition-transform duration-300" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Why Choose Us */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="bg-gray-50 rounded-2xl p-8 md:p-12 mb-16"
          >
            <div className="text-center">
              <h3 className="text-3xl font-heading font-bold text-primary mb-6">Why Choose Segway Capital?</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div>
                  <div className="bg-secondary/10 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <FaShieldAlt className="w-8 h-8 text-secondary" />
                  </div>
                  <h4 className="text-xl font-semibold text-primary mb-3">Regulatory Excellence</h4>
                  <p className="text-gray-600">CIMA licensed and regulated with the highest standards of compliance and client protection.</p>
                </div>
                <div>
                  <div className="bg-secondary/10 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <FaUsers className="w-8 h-8 text-secondary" />
                  </div>
                  <h4 className="text-xl font-semibold text-primary mb-3">Expert Team</h4>
                  <p className="text-gray-600">Experienced professionals with deep knowledge across all aspects of wealth management.</p>
                </div>
                <div>
                  <div className="bg-secondary/10 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <FaCog className="w-8 h-8 text-secondary" />
                  </div>
                  <h4 className="text-xl font-semibold text-primary mb-3">Advanced Technology</h4>
                  <p className="text-gray-600">Cutting-edge platforms and systems for superior execution and client experience.</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="bg-primary text-white rounded-2xl p-8 md:p-12"
          >
            <div className="text-center">
              <h3 className="text-3xl font-heading font-bold mb-6">Ready to Get Started?</h3>
              <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
                Contact our team of experts to discuss how our comprehensive financial services 
                can help you achieve your investment goals and secure your financial future.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link 
                  to="/apply-now" 
                  className="btn btn-secondary text-lg py-4 px-8 shadow-xl hover:shadow-secondary/20 btn-3d inline-block"
                >
                  Apply Now
                </Link>
                <Link 
                  to="/client-login" 
                  className="btn btn-outline-white text-lg py-4 px-8 shadow-xl hover:shadow-white/20 btn-3d inline-block"
                >
                  Client Login
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default ServicesPage; 